/**
 * IntakeSelect — typed React listbox for one intake dimension
 * (industry / size / function).
 *
 * Phase 2 of the pilot React-engine port (see
 * `docs/eng/pilot-react-engine.md`). Replaces the imperative
 * `intake.js` custom listbox. The audit's `nb-intake-menu`
 * Tailwind-hidden-vs-attribute-hidden bug (finding #4) is structurally
 * impossible here: React state is the sole owner of dropdown
 * visibility — there is no second writer to drift against.
 *
 * Native `<select>` is rejected for this surface because the OS popup
 * on macOS Safari/Chrome ignores font-size / padding / max-height and
 * renders an oversized chrome that breaks the Oatmeal taupe visual
 * design. A custom button + listbox preserves the design while keeping
 * the close-on-outside-click and Escape-to-close behaviors the legacy
 * intake.js delivered.
 *
 * The options + placeholder are pulled from the typed catalog in
 * `lib/pilot/processes.ts`; intake state is owned by PilotContext.
 */

import React, {useCallback, useEffect, useId, useRef, useState} from 'react';
import {clsx} from 'clsx/lite';
import {
  FUNCTIONS,
  INDUSTRIES,
  SIZES,
  type DimensionOption,
} from '../../lib/pilot/processes';
import {usePilot, type IntakeField} from './PilotContext';

// localStorage key for the freeform text the visitor types when they
// pick "Other" in a context dropdown. Survives reloads and travels with
// any subsequent lead submission via context.intake.
const OTHER_TEXT_PREFIX = 'mp-intake-other-';

function readOtherText(field: IntakeField): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(OTHER_TEXT_PREFIX + field) || '';
  } catch {
    return '';
  }
}

function writeOtherText(field: IntakeField, text: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (text.trim()) {
      window.localStorage.setItem(OTHER_TEXT_PREFIX + field, text.trim());
    } else {
      window.localStorage.removeItem(OTHER_TEXT_PREFIX + field);
    }
  } catch {
    /* private mode */
  }
}

const FIELD_OPTIONS: Record<IntakeField, ReadonlyArray<DimensionOption>> = {
  industry: INDUSTRIES,
  size: SIZES,
  function: FUNCTIONS,
};

const FIELD_PLACEHOLDER: Record<IntakeField, string> = {
  industry: '— industry —',
  size: '— size —',
  function: '— function —',
};

// Longest option label per field — used as an invisible width sizer
// inside each button so the sentence layout stays stable when the
// visitor picks values (the chip would otherwise grow from "— industry —"
// to "Government / Public Sector" and reflow the surrounding text
// from one line to two).
const FIELD_WIDEST_LABEL: Record<IntakeField, string> = {
  industry: pickWidest(FIELD_PLACEHOLDER.industry, INDUSTRIES),
  size: pickWidest(FIELD_PLACEHOLDER.size, SIZES),
  function: pickWidest(FIELD_PLACEHOLDER.function, FUNCTIONS),
};

function pickWidest(
  placeholder: string,
  options: ReadonlyArray<DimensionOption>,
): string {
  let widest = placeholder;
  for (const o of options) {
    if (o.label.length > widest.length) widest = o.label;
  }
  return widest;
}

function labelFor(field: IntakeField, value: string): string {
  if (!value) return FIELD_PLACEHOLDER[field];
  // When the visitor picks "Other" and types something, show their text
  // in the chip so the choice doesn't read as "you picked nothing".
  if (value === 'other') {
    const txt = readOtherText(field);
    if (txt) return txt;
    return 'Other';
  }
  const opt = FIELD_OPTIONS[field].find(o => o.id === value);
  return opt ? opt.label : FIELD_PLACEHOLDER[field];
}

export interface IntakeSelectProps {
  field: IntakeField;
}

export function IntakeSelect({field}: IntakeSelectProps) {
  const {intake, setIntakeField} = usePilot();
  const value = intake[field];
  const isActive = !!value;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const menuId = useId();

  // Close on outside click — React owns visibility, but the click is
  // a global gesture so we still attach to the window.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const root = containerRef.current;
      if (!root) return;
      const target = e.target as Node | null;
      if (target && root.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = useCallback(
    (next: string) => {
      setIntakeField(field, next);
      setOpen(false);
    },
    [field, setIntakeField],
  );

  const clear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIntakeField(field, '');
    },
    [field, setIntakeField],
  );

  const toggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(prev => !prev);
  }, []);

  return (
    <span
      ref={containerRef}
      className="nb-intake-ctl relative inline-block"
      data-field={field}
    >
      <button
        type="button"
        className={clsx(
          // `inline-grid` (not plain `grid`) keeps the button inline
          // with the surrounding sentence. Both children share
          // `grid-area: 1/1` so the visible label and the invisible
          // widest-label sizer stack in the same cell — the cell
          // sizes to the widest child, so the button stays at the
          // widest-option width regardless of which value is
          // selected and the sentence layout doesn't reflow as the
          // visitor picks values.
          'nb-intake-btn relative inline-grid items-center justify-items-center align-middle rounded-full border px-3 py-1 text-base/6 font-medium',
          isActive
            ? 'nb-intake-btn-active border-taupe-950 bg-taupe-950 text-white dark:border-taupe-100 dark:bg-taupe-100 dark:text-taupe-950'
            : 'border-taupe-300 bg-white text-taupe-950 hover:border-taupe-950 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white',
        )}
        data-field={field}
        data-value={value}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={toggle}
      >
        <span
          aria-hidden
          className="invisible whitespace-nowrap [grid-area:1/1]"
        >
          {FIELD_WIDEST_LABEL[field]}
        </span>
        <span className="whitespace-nowrap [grid-area:1/1]">
          {labelFor(field, value)}
        </span>
      </button>
      {isActive ? (
        <button
          type="button"
          className="nb-intake-clear ml-1 inline-flex size-5 items-center justify-center rounded-full border border-taupe-300 bg-white text-xs font-medium text-taupe-700 hover:border-taupe-950 hover:text-taupe-950 dark:border-taupe-700 dark:bg-taupe-900 dark:text-taupe-400 dark:hover:text-white"
          aria-label={`Clear ${field}`}
          onClick={clear}
        >
          ×
        </button>
      ) : null}
      {value === 'other' ? (
        <OtherTextInput field={field} />
      ) : null}
      {open ? (
        <ul
          id={menuId}
          role="listbox"
          aria-label={field}
          className="nb-intake-menu absolute left-0 top-full z-20 mt-2 flex max-h-72 min-w-48 flex-col gap-1 overflow-auto rounded-xl border border-taupe-200 bg-white p-2 shadow-lg dark:border-taupe-800 dark:bg-taupe-900"
        >
          <li
            role="option"
            aria-selected={value === ''}
            data-value=""
            className="nb-intake-option flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-taupe-900 transition-colors hover:bg-taupe-100 focus-visible:bg-taupe-100 focus-visible:outline-none dark:text-taupe-100 dark:hover:bg-taupe-800 dark:focus-visible:bg-taupe-800"
            onClick={() => choose('')}
          >
            — any —
          </li>
          {FIELD_OPTIONS[field].map(opt => (
            <li
              key={opt.id}
              role="option"
              aria-selected={value === opt.id}
              data-value={opt.id}
              className="nb-intake-option flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-taupe-900 transition-colors hover:bg-taupe-100 focus-visible:bg-taupe-100 focus-visible:outline-none dark:text-taupe-100 dark:hover:bg-taupe-800 dark:focus-visible:bg-taupe-800"
              onClick={() => choose(opt.id)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}

/**
 * OtherTextInput — inline text field that appears alongside the chip
 * when the visitor picks "Other" in a context dropdown. Persists to
 * localStorage so the value survives reload + travels with subsequent
 * lead submissions. Re-renders the parent chip when changed so the
 * typed label shows in the chip itself.
 */
function OtherTextInput({field}: {field: IntakeField}) {
  const {setIntakeField, intake} = usePilot();
  const [text, setText] = useState<string>(() => readOtherText(field));
  // Mirror the localStorage state back into PilotContext so the chip
  // re-renders with the typed label. We round-trip through the same
  // intake field id (value stays "other") and trigger a re-render by
  // bouncing the field id through itself.
  const commit = (next: string) => {
    setText(next);
    writeOtherText(field, next);
    // Force re-render of parent IntakeSelect by re-setting the field
    // to its current value. The chip label reads via readOtherText().
    setIntakeField(field, intake[field]);
  };
  return (
    <input
      type="text"
      value={text}
      onChange={(e) => commit(e.target.value)}
      placeholder={`Describe your ${field}`}
      aria-label={`Describe your ${field}`}
      className="nb-intake-other ml-2 inline-block w-44 max-w-[60vw] rounded-full border border-taupe-300 bg-white px-3 py-1 text-sm text-taupe-950 placeholder:text-taupe-500 focus:border-taupe-950 focus:outline-none focus:ring-1 focus:ring-taupe-300 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
    />
  );
}

export default IntakeSelect;
